// frontend/src/pages/citizen/CitizenPrescriptions.js
// Updated for comprehensive-services return flow, follow-up planning, citizen reminders,
// and post-visit training suggestions while preserving the original approval pattern.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography, TextField, Grid, Alert, CircularProgress, Divider,
  IconButton, Button, Box, Paper, InputAdornment, Chip, Tooltip,
  Checkbox, FormGroup, FormControl, FormLabel, FormControlLabel, MenuItem
} from '@mui/material';
import { Add, Delete, Search } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import EncounterHeader from '../../components/EncounterHeader';
import useVisitReadOnly from '../../useVisitReadOnly';
import http from '../../services/http';
import medicineList from '../../data/medicineList';
import { buildMedicationSupport, getVisitWeightKg } from '../../clinical/medicationDecisionSupport';
import { getSectionModulePlan, moduleLabels } from '../../clinical/visitSectionModules';

function useQuery(){ const { search } = useLocation(); return useMemo(() => new URLSearchParams(search), [search]); }
function jget(k){ try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } }

// ✅ tenant-safe navigation (prevents falling out of /t/:slug -> login redirect)
function tenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}
function tenantize(path) {
  const base = tenantBase();
  const p = String(path || '');
  if (!base) return p;
  if (!p.startsWith('/')) return `${base}/${p}`.replace(/\/{2,}/g, '/');
  if (p.startsWith(base)) return p;
  return `${base}${p}`.replace(/\/{2,}/g, '/');
}

const CREDIT_STATUS_OPTIONS = [
  { value: 'advised', label: 'Only advised' },
  { value: 'observed', label: 'Observed' },
  { value: 'assisted', label: 'Assisted' },
  { value: 'performed_under_supervision', label: 'Performed under supervision' },
  { value: 'performed_independently', label: 'Performed independently' },
];

const PROCEDURE_DEFINITIONS = [
  {
    key: 'suturing',
    label: 'Suturing done',
    shortLabel: 'Suturing',
    steps: [
      'Indication confirmed and wound assessed',
      'Aseptic preparation ensured',
      'Wound cleaned / irrigated',
      'Debridement done if needed',
      'Suturing performed / assisted',
      'Dressing applied and aftercare explained',
    ],
  },
  {
    key: 'dressing',
    label: 'Dressing done',
    shortLabel: 'Dressing',
    steps: [
      'Wound examined',
      'Cleaning / irrigation done',
      'Antiseptic applied if needed',
      'Appropriate dressing material selected',
      'Dressing secured properly',
      'Follow-up / warning advice explained',
    ],
  },
  {
    key: 'bandage',
    label: 'Bandage applied',
    shortLabel: 'Bandage application',
    steps: [
      'Site / injury assessed',
      'Correct bandage type selected',
      'Bandage applied with proper support',
      'Distal circulation / comfort checked',
      'Home care advice explained',
    ],
  },
  {
    key: 'imInjection',
    label: 'IM injection',
    shortLabel: 'IM injection',
    steps: [
      'Drug and dose verified',
      'Correct IM site identified',
      'Aseptic precautions followed',
      'Injection administered / assisted',
      'Immediate post-injection advice given',
    ],
  },
  {
    key: 'ivInjection',
    label: 'IV injection',
    shortLabel: 'IV injection',
    steps: [
      'Drug / fluid indication confirmed',
      'Correct line / site assessed',
      'Aseptic precautions followed',
      'IV administration completed / assisted',
      'Monitoring / immediate advice recorded',
    ],
  },
  {
    key: 'tdGiven',
    label: 'Td given',
    shortLabel: 'Td administration',
    steps: [
      'Tetanus indication checked',
      'Dose / schedule verified',
      'Citizen informed / counseled',
      'Injection administered / assisted',
      'Advice on next dose / warning signs given',
    ],
  },
  {
    key: 'nebulization',
    label: 'Nebulization given',
    shortLabel: 'Nebulization',
    steps: [
      'Indication and drug selected',
      'Device prepared correctly',
      'Procedure completed / assisted',
      'Response reassessed',
      'Home advice / referral advice provided',
    ],
  },
  {
    key: 'rbsChecked',
    label: 'RBS checked',
    shortLabel: 'Random blood sugar',
    steps: [
      'Indication explained',
      'Strip / device prepared',
      'Sample taken safely',
      'Value recorded',
      'Citizen informed and advised',
    ],
  },
];

const COUNSELING_DEFINITIONS = [
  {
    key: 'generalCounseling',
    label: 'General counseling given',
    shortLabel: 'General counseling',
    steps: [
      'Problem explained in simple language',
      'Medicine use explained',
      'Diet / rest / hydration advice given',
      'Warning signs explained',
      'Follow-up advised',
    ],
  },
  {
    key: 'diseaseSpecificCounseling',
    label: 'Disease-specific counseling',
    shortLabel: 'Disease-specific counseling',
    steps: [
      'Diagnosis / likely condition explained',
      'Condition-specific precautions explained',
      'Home care instructions given',
      'Danger signs explained',
      'Follow-up / review plan explained',
    ],
  },
  {
    key: 'medicationAdherence',
    label: 'Medication adherence counseling',
    shortLabel: 'Medication adherence',
    steps: [
      'Drug schedule explained',
      'Dose / frequency explained',
      'Importance of compliance reinforced',
      'Common side effects explained',
      'Missed-dose advice given',
    ],
  },
  {
    key: 'dietLifestyle',
    label: 'Diet / lifestyle counseling',
    shortLabel: 'Diet / lifestyle counseling',
    steps: [
      'Relevant diet advice provided',
      'Lifestyle / activity advice given',
      'Tobacco / alcohol / risk behavior advice given if relevant',
      'Citizen understanding checked',
    ],
  },
  {
    key: 'dangerSigns',
    label: 'Danger signs explained',
    shortLabel: 'Danger signs counseling',
    steps: [
      'Red-flag symptoms explained',
      'Need for urgent return explained',
      'Referral trigger explained',
    ],
  },
  {
    key: 'followUpAdvice',
    label: 'Follow-up advice given',
    shortLabel: 'Follow-up advice',
    steps: [
      'Review timeline explained',
      'Repeat visit / test advice given',
      'Citizen informed when to come earlier',
    ],
  },
  {
    key: 'referralAdvice',
    label: 'Referral advice given',
    shortLabel: 'Referral advice',
    steps: [
      'Reason for referral explained',
      'Urgency explained',
      'Referral destination / next step explained',
    ],
  },
];

const FOLLOW_UP_DEFINITIONS = [
  { key: 'physical', label: 'Physical follow-up', shortLabel: 'Physical review', defaultMode: 'physical_follow_up' },
  { key: 'audio', label: 'Audio telemedicine follow-up', shortLabel: 'Audio follow-up', defaultMode: 'audio_follow_up' },
  { key: 'video', label: 'Video telemedicine follow-up', shortLabel: 'Video follow-up', defaultMode: 'video_follow_up' },
  { key: 'homeVisit', label: 'Home visit follow-up', shortLabel: 'Home visit', defaultMode: 'home_visit' },
];

const HEALTH_REMINDER_DEFINITIONS = [
  { key: 'footCare', label: 'Foot care reminder' },
  { key: 'physicalActivity', label: 'Physical activity reminder' },
  { key: 'bloodSugarCheck', label: 'Blood sugar check reminder' },
  { key: 'bpCheck', label: 'Blood pressure check reminder' },
  { key: 'woundCare', label: 'Wound care / dressing reminder' },
  { key: 'hydration', label: 'Hydration reminder' },
  { key: 'dietAction', label: 'Diet-related reminder' },
  { key: 'followUpVisit', label: 'Follow-up visit reminder' },
  { key: 'telemedicineAppointment', label: 'Telemedicine appointment reminder' },
  { key: 'homeVisitPreparation', label: 'Home visit reminder' },
];

const PRIMARY_VISIT_SUGGESTION_MAP = {
  'Under-5 / Child Health': {
    title: 'Child health assessment',
    description: 'Suitable when the encounter focused on child symptoms, growth, nutrition, or immunization-related advice.',
  },
  'Antenatal Care': {
    title: 'ANC assessment',
    description: 'Suitable when antenatal review, examination, treatment, or counseling was completed in this visit.',
  },
  'Postnatal Care': {
    title: 'Postnatal assessment',
    description: 'Suitable when mother/newborn review and postnatal counseling were completed in this visit.',
  },
  'Adolescent Health': {
    title: 'Adolescent clinic encounter',
    description: 'Suitable when adolescent-focused history, examination, counseling, or follow-up planning was part of this encounter.',
  },
  'Adult Medicine': {
    title: 'Common adult OPD assessment',
    description: 'Suitable when the visit involved adult OPD evaluation and treatment planning.',
  },
  'Geriatric Care': {
    title: 'Geriatric assessment / clinic encounter',
    description: 'Suitable when geriatric review, functional advice, or chronic disease review was completed.',
  },
  'NCD Screening / Follow-up': {
    title: 'NCD follow-up assessment',
    description: 'Suitable for diabetes / hypertension / lifestyle follow-up done in this encounter.',
  },
  'Occupational Health': {
    title: 'Occupational health assessment',
    description: 'Suitable when occupational exposure review or counseling was part of this encounter.',
  },
  'LGBTQIA+ Health / Counseling': {
    title: 'LGBTQIA+ health / counseling encounter',
    description: 'Suitable when supportive counseling or focused health review relevant to LGBTQIA+ care was part of the visit.',
  },
};

function getAgeNumber(val){
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  const s = String(val ?? '').trim();
  if (!s) return null;
  const m = s.match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

function normalizeGender(val){
  const s = String(val || '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (['male', 'm', 'man', 'boy'].includes(s)) return 'male';
  if (['female', 'f', 'woman', 'girl'].includes(s)) return 'female';
  if (s.includes('trans')) return 'transgender';
  if (s.includes('non-binary') || s.includes('non binary') || s.includes('nonbinary')) return 'other';
  return s;
}

function getAgeGroup(age){
  if (age == null) return 'unknown';
  if (age <= 4) return 'under5';
  if (age <= 9) return 'child';
  if (age <= 19) return 'adolescent';
  if (age <= 59) return 'adult';
  return 'geriatric';
}

function ageGroupLabel(group){
  if (group === 'under5') return 'Under-5';
  if (group === 'child') return '5–9 years';
  if (group === 'adolescent') return '10–19 years';
  if (group === 'adult') return '20–59 years';
  if (group === 'geriatric') return '60+ years';
  return 'Not derived';
}

function formatAgeAtVisit(visit = {}, fallbackYears = null) {
  const days = Number(visit?.ageInDaysAtVisit);
  if (Number.isFinite(days) && days >= 0) {
    if (days < 60) {
      const wholeDays = Math.floor(days);
      return `${wholeDays} day${wholeDays === 1 ? '' : 's'}`;
    }
    if (days < 365.2425) {
      const months = Math.floor(days / 30.4375);
      const remainingDays = Math.max(0, Math.floor(days - (months * 30.4375)));
      return `${months} month${months === 1 ? '' : 's'}${remainingDays ? ` ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''}`;
    }
    const years = Math.floor(days / 365.2425);
    const remainingDays = days - Math.floor(years * 365.2425);
    const months = Math.max(0, Math.floor(remainingDays / 30.4375));
    return `${years} year${years === 1 ? '' : 's'}${months ? ` ${months} month${months === 1 ? '' : 's'}` : ''}`;
  }
  if (fallbackYears == null || !Number.isFinite(Number(fallbackYears))) return '';
  return `${Math.floor(Number(fallbackYears))} years`;
}

function normalizeFollowUpPlan(raw){
  const source = raw && typeof raw === 'object' ? raw : {};
  return FOLLOW_UP_DEFINITIONS.reduce((acc, def) => {
    const existing = source[def.key] && typeof source[def.key] === 'object' ? source[def.key] : {};
    acc[def.key] = {
      enabled: !!existing.enabled,
      date: String(existing.date || ''),
      time: String(existing.time || ''),
      reason: String(existing.reason || ''),
      notes: String(existing.notes || ''),
      priority: String(existing.priority || 'routine'),
      mode: String(existing.mode || def.defaultMode),
    };
    return acc;
  }, {});
}

function normalizeMedicationReminders(raw, medicines){
  const source = Array.isArray(raw) ? raw : [];
  const meds = Array.isArray(medicines) ? medicines : [];
  return meds.map((medicine, index) => {
    const existing = source[index] && typeof source[index] === 'object' ? source[index] : {};
    const times = Array.isArray(existing.times) ? existing.times.slice(0, 4) : [];
    while (times.length < 4) times.push('');
    return {
      enabled: !!existing.enabled,
      medicine: String(existing.medicine || medicine || ''),
      frequency: String(existing.frequency || 'daily'),
      times: times.map((x) => String(x || '')),
      notes: String(existing.notes || ''),
    };
  });
}

function normalizeHealthReminders(raw){
  const source = raw && typeof raw === 'object' ? raw : {};
  return HEALTH_REMINDER_DEFINITIONS.reduce((acc, def) => {
    const existing = source[def.key] && typeof source[def.key] === 'object' ? source[def.key] : {};
    acc[def.key] = {
      enabled: !!existing.enabled,
      frequency: String(existing.frequency || 'daily'),
      time: String(existing.time || ''),
      notes: String(existing.notes || ''),
    };
    return acc;
  }, {});
}

function syncMedicationReminders(reminders, medicines){
  return normalizeMedicationReminders(reminders, medicines);
}

function createPostVisitSuggestions({ visitCategory, visitType, procedureDefs, counselingDefs, medicationRemindersEnabled }){
  const normalizedCategory = String(visitCategory || '').trim();
  const primary = PRIMARY_VISIT_SUGGESTION_MAP[normalizedCategory] || {
    title: 'Outpatient assessment and management of common ailment',
    description: 'Suitable for a completed OPD encounter where history, examination, investigations, and treatment planning were documented.',
  };

  const optional = [];
  (Array.isArray(procedureDefs) ? procedureDefs : []).forEach((item) => {
    optional.push({
      key: `procedure:${item.key}`,
      title: `${item.shortLabel} skill / procedure log`,
      reason: `${item.label} was recorded in the visit care record.`,
    });
  });
  (Array.isArray(counselingDefs) ? counselingDefs : []).forEach((item) => {
    optional.push({
      key: `counseling:${item.key}`,
      title: `${item.shortLabel} competency log`,
      reason: `${item.label} was recorded in the visit care record.`,
    });
  });
  if (medicationRemindersEnabled) {
    optional.push({
      key: 'followup:medication-reminders',
      title: 'Citizen medicine adherence / reminder setup',
      reason: 'Medication reminders were enabled for this encounter.',
    });
  }
  if (/follow-up/i.test(String(visitType || ''))) {
    optional.push({
      key: 'visit:follow-up-competency',
      title: 'Follow-up planning competency',
      reason: 'This encounter was classified as a follow-up visit.',
    });
  }
  return { primary, optional: optional.slice(0, 6) };
}

function getRecommendedProcedureKeys({ visitCategory, clinicalPathway, complaintGroup, ageGroup, citizenGender, activeClinicalModules = [] }){
  const hay = `${visitCategory} ${clinicalPathway} ${complaintGroup}`.toLowerCase();
  const modules = new Set(activeClinicalModules || []);
  const set = new Set();
  if (modules.has('injury') || modules.has('wound') || /wound|injury|trauma|cut|laceration|burn|dressing|bandage/.test(hay)) {
    set.add('dressing');
    set.add('bandage');
    set.add('suturing');
  }
  if ((modules.has('maternal') || /injection|immunization|vaccination|td|anc|postnatal/.test(hay)) && citizenGender !== 'male') {
    set.add('imInjection');
    set.add('tdGiven');
  }
  if (modules.has('immunization') || /immunization|vaccination/.test(hay)) set.add('imInjection');
  if (modules.has('respiratory') || /respiratory|cough|wheeze|breath|asthma|copd/.test(hay)) set.add('nebulization');
  if (modules.has('ncd') || modules.has('metabolic') || /diabetes|blood sugar|ncd|hypertension/.test(hay)) set.add('rbsChecked');
  if (modules.has('dehydration') || /iv|dehydration|fluid/.test(hay)) set.add('ivInjection');
  if (ageGroup === 'under5' && (modules.has('immunization') || /immunization|child/.test(hay))) set.add('imInjection');
  return Array.from(set);
}

function getRecommendedCounselingKeys({ visitCategory, clinicalPathway, complaintGroup, ageGroup, citizenGender, activeClinicalModules = [] }){
  const hay = `${visitCategory} ${clinicalPathway} ${complaintGroup}`.toLowerCase();
  const modules = new Set(activeClinicalModules || []);
  const set = new Set(['generalCounseling', 'dangerSigns', 'followUpAdvice']);
  if (modules.has('ncd') || modules.has('geriatric') || /diabetes|hypertension|ncd|geriatric/.test(hay)) {
    set.add('medicationAdherence');
    set.add('dietLifestyle');
    set.add('diseaseSpecificCounseling');
  }
  if (modules.has('occupational') || /occupational/.test(hay)) {
    set.add('diseaseSpecificCounseling');
  }
  if ((modules.has('maternal') || modules.has('newborn') || /antenatal|anc|postnatal|pnc|mother|newborn/.test(hay)) && citizenGender !== 'male') {
    set.add('diseaseSpecificCounseling');
  }
  if (modules.has('paediatric') || modules.has('immunization') || /child|under-5|immunization/.test(hay) || ageGroup === 'under5') {
    set.add('diseaseSpecificCounseling');
  }
  if (modules.has('red-flag') || /referral|red flag|emergency/.test(hay)) set.add('referralAdvice');
  return Array.from(set);
}

function getRecommendedMedicineCategories({ visitCategory, clinicalPathway, complaintGroup, ageGroup, activeClinicalModules = [] }){
  const hay = `${visitCategory} ${clinicalPathway} ${complaintGroup}`.toLowerCase();
  const modules = new Set(activeClinicalModules || []);
  const out = new Set();
  if (modules.has('paediatric') || /child|under-5/.test(hay) || ageGroup === 'under5') out.add('Paediatric');
  if (modules.has('ncd') || modules.has('metabolic') || modules.has('cardiovascular') || /diabetes|hypertension|ncd|cardio/.test(hay)) out.add('Non‑communicable');
  if (modules.has('respiratory') || /respiratory|cough|wheeze|asthma|copd/.test(hay)) out.add('Respiratory');
  if (modules.has('musculoskeletal') || modules.has('injury') || /pain|musculoskeletal|injury|trauma/.test(hay)) out.add('Musculoskeletal');
  if (modules.has('dermatology') || modules.has('wound') || /skin|allergy|wound/.test(hay)) out.add('Dermatology');
  if (modules.has('gastrointestinal') || /gi|gastro|vomiting|diarrhea/.test(hay)) out.add('Gastrointestinal');
  if (modules.has('neurological') || modules.has('mental-health')) out.add('Neurology/Psychiatry');
  if (modules.has('maternal')) out.add('Pregnancy');
  if (modules.has('fever-infection') || modules.has('infection')) out.add('Infectious');
  if (modules.has('red-flag') || modules.has('toxicology') || modules.has('heat')) out.add('Emergency');
  return Array.from(out);
}

function normalizeCareLog(definitions, raw){
  const source = raw && typeof raw === 'object' ? raw : {};
  return definitions.reduce((acc, def) => {
    const existing = source[def.key] && typeof source[def.key] === 'object' ? source[def.key] : {};
    const rawSteps = existing.steps && typeof existing.steps === 'object' ? existing.steps : {};
    const normalizedSteps = def.steps.reduce((stepAcc, stepLabel) => {
      stepAcc[stepLabel] = !!rawSteps[stepLabel];
      return stepAcc;
    }, {});
    acc[def.key] = {
      done: !!existing.done,
      status: String(existing.status || 'advised'),
      notes: String(existing.notes || ''),
      steps: normalizedSteps,
    };
    return acc;
  }, {});
}

function careLogHasSelections(log){
  if (!log || typeof log !== 'object') return false;
  return Object.values(log).some((item) => item && item.done);
}

function careLogCompletedCount(log){
  if (!log || typeof log !== 'object') return 0;
  return Object.values(log).filter((item) => item && item.done).length;
}

function medicineEditorText(medicine){
  if (medicine == null) return '';
  if (typeof medicine === 'string' || typeof medicine === 'number') return String(medicine);
  if (typeof medicine !== 'object') return String(medicine);

  const drug = String(medicine.drug || medicine.name || medicine.medicine || '').trim();
  const details = [
    medicine.dose,
    medicine.frequency || medicine.sig,
    medicine.duration,
    medicine.route,
  ].map((value) => String(value || '').trim()).filter(Boolean);

  if (!drug && !details.length) return '';
  return [drug, ...details].filter(Boolean).join(' — ');
}

function unwrapPrescriptionPayload(payload){
  const value = payload?.data ?? payload ?? {};
  return (
    value?.prescription ||
    value?.prescriptions ||
    value?.data?.prescription ||
    value?.data?.prescriptions ||
    value?.data ||
    value ||
    {}
  );
}

function ensurePrescription(p){
  const preset = p || {};
  const meds = Array.isArray(preset.medicines)
    ? preset.medicines.map(medicineEditorText)
    : [];
  const icdRaw = Array.isArray(preset.icdCodes) ? preset.icdCodes : [];
  const icdCodes = icdRaw.map((c) => {
    if (c && typeof c === 'object') {
      const t = (c.title && String(c.title)) || (c.code != null ? String(c.code) : '');
      return { title: t };
    }
    return { title: String(c || '') };
  });
  return {
    medicines: meds,
    advice: preset.advice || '',
    notes: preset.notes || '',
    differentialDx: preset.differentialDx || '',
    icdCodes,
    opdProcedureLog: normalizeCareLog(PROCEDURE_DEFINITIONS, preset.opdProcedureLog),
    opdCounselingLog: normalizeCareLog(COUNSELING_DEFINITIONS, preset.opdCounselingLog),
    followUpPlan: normalizeFollowUpPlan(preset.followUpPlan),
    medicationRemindersEnabled: !!preset.medicationRemindersEnabled,
    medicationReminderNotes: String(preset.medicationReminderNotes || ''),
    medicationReminders: normalizeMedicationReminders(preset.medicationReminders, meds),
    healthReminders: normalizeHealthReminders(preset.healthReminders),
    status: String(preset.status || 'draft').toLowerCase(),
    updatedAt: preset.updatedAt || null,
    submittedAt: preset.submittedAt || null,
    returnedAt: preset.returnedAt || null,
    approvedAt: preset.approvedAt || null,
    returnNote: preset.returnNote || '',
  };
}

async function tryGet(urls){
  let lastErr = null;
  for (const url of urls){
    try {
      const r = await http.get(url);
      if (r && r.status >= 200 && r.status < 300) return r;
      lastErr = new Error(r?.data?.message || 'Failed to load prescription');
    } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('Failed to load prescription');
}
async function trySave(urls, method, body){
  let lastErr = null;
  for (const url of urls){
    try {
      const r = method === 'PUT' ? await http.put(url, body) : await http.post(url, body);
      if (r && r.status >= 200 && r.status < 300) return r;
      lastErr = new Error((r && r.data && r.data.message) || 'Save failed');
    } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('Save failed');
}
async function patchApproveOrSubmit(patchUrl, body, legacyPostUrl){
  try { return await http.patch(patchUrl, body); }
  catch { return await http.post(legacyPostUrl, body); }
}

// FAB coordination
const setCanContinue = (enabled) => { try { localStorage.setItem('canContinue', enabled ? '1' : '0'); } catch {} };
const broadcastEnable = (enabled, visitId, extra = {}) => {
  setCanContinue(!!enabled);
  const detail = { canContinue: !!enabled, enabled: !!enabled, visitId, section: 'prescription', ...extra };
  const fire = (name, d = detail) => { try { window.dispatchEvent(new CustomEvent(name, { detail: d })); } catch {} };
  fire('encounter:can-continue');
  fire('visit:can-continue');
  fire('fab:enable', { enabled: !!enabled, visitId });
  fire('fab:show', { visible: true, visitId });
  fire('encounter:dirty', { dirty: !!enabled, visitId });
  try { window.postMessage({ type: 'fab:enable', enabled: !!enabled, visitId }, '*'); } catch {}
};

export default function CitizenPrescriptions({
  embedded = false,
  visitId: visitIdProp = '',
  citizenId: citizenIdProp = '',
  visit: visitProp = null,
  onSectionDirty,
  onSectionSaved,
  onCanSave,
  onSavingChange,
  registerSave,
} = {}){
  const theme = useTheme(); const query = useQuery(); const params = useParams(); const navigate = useNavigate();
  const user = jget('loggedInUser') || {}; const role = String(user.role || user.userType || '').toLowerCase();
  const isIntern = role.includes('intern'); const isCitizen = role === 'citizen';

  const [visitId, setVisitId] = useState(() => (
    embedded ? String(visitIdProp || '').trim() : ''
  ));
  const [citizenId, setCitizenId] = useState(() => (
    embedded ? String(citizenIdProp || '').trim() : ''
  ));
  const [form, setForm] = useState(ensurePrescription({}));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogCat, setCatalogCat] = useState('All');

  const activeCitizenData = useMemo(() => jget('activeCitizen') || {}, []);
  const resolvedCitizen = useMemo(() => ({
    ...activeCitizenData,
    citizenId:
      String(visitProp?.citizenId || citizenIdProp || activeCitizenData?.citizenId || '').trim(),
    name: visitProp?.citizenName || activeCitizenData?.name || '',
    age:
      visitProp?.ageYearsAtVisit ??
      visitProp?.citizenAge ??
      activeCitizenData?.age ??
      '',
    gender: visitProp?.citizenGender || activeCitizenData?.gender || '',
  }), [activeCitizenData, citizenIdProp, visitProp]);

  const citizenAge = useMemo(() => getAgeNumber(resolvedCitizen?.age), [resolvedCitizen]);
  const citizenGender = useMemo(() => normalizeGender(resolvedCitizen?.gender), [resolvedCitizen]);
  const citizenAgeGroup = useMemo(() => getAgeGroup(citizenAge), [citizenAge]);

  const fieldProps = theme.palette.mode === 'dark'
    ? { InputLabelProps: { style: { color: '#fff' } }, inputProps: { style: { color: '#fff' } } }
    : { InputLabelProps: { style: { color: 'inherit' } } };

  // Resolve visit/citizen. In Workspace mode the persisted Visit and route
  // parameters are authoritative; the standalone editor keeps its URL/storage
  // compatibility path.
  useEffect(() => {
    if (embedded) {
      const cid = String(citizenIdProp || visitProp?.citizenId || '').trim();
      const vid = String(visitIdProp || visitProp?.visitId || '').trim();

      setCitizenId(cid);
      setVisitId(vid);

      if (cid && vid) {
        try {
          localStorage.setItem('visitId', vid);
          localStorage.setItem('activeVisitId', vid);
          localStorage.setItem('activeVisitId:' + cid, vid);
          localStorage.setItem('activeCitizenId', cid);
        } catch {}
      }
      return;
    }

    const urlVisitId = query.get('visitId') || '';
    const urlCitizenId = query.get('citizenId') || params.citizenId || '';
    const activeCitizen = jget('activeCitizen') || {};
    const cid = urlCitizenId || activeCitizen.citizenId || localStorage.getItem('activeCitizenId') || localStorage.getItem('citizenId') || '';
    const scoped = cid ? localStorage.getItem('activeVisitId:' + cid) : '';
    const legacy = localStorage.getItem('activeVisitId') || localStorage.getItem('visitId') || '';
    const vid = urlVisitId || scoped || legacy || '';
    setCitizenId(cid || ''); setVisitId(vid || '');
    if (cid && vid && scoped === vid && !urlVisitId){
      localStorage.setItem('visitId', vid);
      localStorage.setItem('activeVisitId', vid);
      localStorage.setItem('activeVisitId:' + cid, vid);
    }
  }, [
    embedded,
    citizenIdProp,
    visitIdProp,
    visitProp?.citizenId,
    visitProp?.visitId,
    params.citizenId,
    query,
  ]);

  // read-only gate
  const readOnlyByVisit = useVisitReadOnly(visitId, citizenId);
  const statusLower = String(form?.status || '').toLowerCase();
  const isSubmittedOrApproved = statusLower === 'submitted' || statusLower === 'approved';
  const statusIsReturned = statusLower === 'returned';
  const editFlag = (typeof window !== 'undefined' && localStorage.getItem('editReturnedVisit')) || '';
  const allowInternEditReturned = isIntern && visitId && (statusIsReturned || editFlag === visitId);
  const readOnly = isCitizen || (readOnlyByVisit && !allowInternEditReturned) || isSubmittedOrApproved;
  const disabled = readOnly;

  // ✅ compute “has data” BEFORE any effect references it (fixes runtime ReferenceError)
  const procedureSelectionCount = useMemo(() => careLogCompletedCount(form.opdProcedureLog), [form.opdProcedureLog]);
  const counselingSelectionCount = useMemo(() => careLogCompletedCount(form.opdCounselingLog), [form.opdCounselingLog]);

  const hasData = useMemo(() => {
    const meds = Array.isArray(form.medicines) && form.medicines.some(m => String(m || '').trim());
    const adv = String(form.advice || '').trim();
    const nts = String(form.notes || '').trim();
    const ddx = String(form.differentialDx || '').trim();
    const icd = Array.isArray(form.icdCodes) && form.icdCodes.some(x => String((x && x.title) || '').trim());
    const procedures = careLogHasSelections(form.opdProcedureLog);
    const counseling = careLogHasSelections(form.opdCounselingLog);
    return meds || adv || nts || ddx || icd || procedures || counseling;
  }, [form]);

  // Standalone editor keeps the historical FAB contract. Workspace mode has
  // its own section Save button and must not advertise submit/approve actions.
  useEffect(() => {
    if (embedded) return undefined;
    const onFabQuery = () => broadcastEnable(!disabled && hasData && !isSubmittedOrApproved, visitId, { reason: 'fab:query' });
    window.addEventListener('fab:query', onFabQuery);
    return () => window.removeEventListener('fab:query', onFabQuery);
  }, [embedded, disabled, hasData, visitId, isSubmittedOrApproved]);

  // load current Rx
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!visitId){ setLoading(false); return; }
      setLoading(true); setLoadError('');
      try{
        const r = await tryGet([
          `/api/visits/${encodeURIComponent(visitId)}/prescriptions`,
          `/api/visits/${encodeURIComponent(visitId)}/prescription`,
          `/api/visit-prescription/${encodeURIComponent(visitId)}`
        ]);
        if (!mounted) return;
        setForm(ensurePrescription(unwrapPrescriptionPayload(r)));
      }catch(e){ if (mounted) setLoadError(e.message || 'Could not load prescription'); }
      finally{ if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [visitId]);

  const setBusy = useCallback((busy) => {
    if (typeof onSavingChange === 'function') onSavingChange(Boolean(busy));
    try {
      window.dispatchEvent(new CustomEvent('encounter:busy',{
        detail:{ busy: Boolean(busy), visitId, section: 'prescription' }
      }));
    } catch {}
  }, [onSavingChange, visitId]);

  useEffect(() => {
    const enabled =
      !loading &&
      !disabled &&
      !isSubmittedOrApproved &&
      Boolean(visitId && citizenId) &&
      (embedded || hasData);

    if (typeof onCanSave === 'function') onCanSave(enabled);
    if (!embedded) broadcastEnable(enabled, visitId, { reason: 'hasData' });
  }, [
    citizenId,
    disabled,
    embedded,
    hasData,
    isSubmittedOrApproved,
    loading,
    onCanSave,
    visitId,
  ]);

  useEffect(() => {
    if (embedded) return undefined;
    const iv = setInterval(() => broadcastEnable(!disabled && hasData && !isSubmittedOrApproved, visitId, { reason: 'heartbeat' }), 1500);
    return () => clearInterval(iv);
  }, [embedded, visitId, hasData, disabled, isSubmittedOrApproved]);

  const markDirty = useCallback((extra={}) => {
    if (isSubmittedOrApproved) return;
    if (typeof onSectionDirty === 'function') onSectionDirty(extra);
    try {
      window.dispatchEvent(new CustomEvent('encounter:dirty', {
        detail: { dirty: true, visitId, section: 'prescription', ...extra }
      }));
    } catch {}
    if (!embedded) {
      broadcastEnable(!disabled && !isSubmittedOrApproved, visitId, { reason: 'typing' });
    }
  }, [disabled, embedded, isSubmittedOrApproved, onSectionDirty, visitId]);

  // Section save is deliberately separate from submit/approve. In Workspace
  // mode this is the only action exposed by the Prescription tab.
  const save = useCallback(async () => {
    try{
      if (readOnly || !visitId || !citizenId || isSubmittedOrApproved) return false;
      setBusy(true); setSaveError(''); setSaveSuccess('');
      const payload = ensurePrescription(form);
      const response = await trySave([
        `/api/visits/${encodeURIComponent(visitId)}/prescriptions`,
        `/api/visits/${encodeURIComponent(visitId)}/prescription`,
        `/api/visit-prescription/${encodeURIComponent(visitId)}`
      ], 'PUT', { citizenId, prescription: payload });

      const saved = unwrapPrescriptionPayload(response);
      if (saved && typeof saved === 'object') {
        setForm(ensurePrescription(saved));
      }

      setSaveSuccess('Prescription saved'); setLoadError('');
      if (typeof onSectionSaved === 'function') {
        await onSectionSaved(response?.data || response);
      }
      return true;
    }catch(e){ setSaveError(e.message || 'Save failed'); return false; }
    finally{ setBusy(false); }
  }, [
    citizenId,
    form,
    isSubmittedOrApproved,
    onSectionSaved,
    readOnly,
    setBusy,
    visitId,
  ]);

  useEffect(() => {
    if (typeof registerSave !== 'function') return undefined;
    registerSave(save);
    return () => registerSave(null);
  }, [registerSave, save]);

  // helper: clear only active visit context; preserve selected citizen/centre for the operations hub
  const clearScopedVisitContext = useCallback(() => {
    try {
      const cid = citizenId || localStorage.getItem('activeCitizenId') || localStorage.getItem('citizenId');
      if (cid) localStorage.removeItem(`activeVisitId:${cid}`);
      localStorage.removeItem('activeVisitId');
      localStorage.removeItem('visitId');
      localStorage.removeItem('editReturnedVisit');
      try {
        const existingCitizen = jget('activeCitizen');
        if (existingCitizen && cid) {
          localStorage.setItem('activeCitizenId', cid);
          localStorage.setItem('citizenId', cid);
          localStorage.setItem('activeCitizen', JSON.stringify(existingCitizen));
        }
      } catch {}
    } catch {}
  }, [citizenId]);

  // submit (FAB triggers this for interns)
  const sendForApproval = useCallback(async () => {
    if (!isIntern || isSubmittedOrApproved) return;
    try{
      setActionError(''); setActionSuccess(''); setBusy(true);
      const ok = await save(); if (!ok) throw new Error('Please complete required fields');

      const actor = user?.name || user?.fullName || user?.email || '';
      const patchBodies = { actor, role: user?.role || user?.userType || '' };
      await patchApproveOrSubmit(
        `/api/visits/${encodeURIComponent(visitId)}/prescription/submit`,
        patchBodies,
        `/api/visit-prescription/${encodeURIComponent(visitId)}/submit`
      );

      const wasReturnFlow = statusIsReturned || (localStorage.getItem('editReturnedVisit') === visitId);
      try { localStorage.removeItem('editReturnedVisit'); } catch {}

      setForm(f => ({ ...f, status: 'submitted' }));
      setActionSuccess('Sent for approval');

      // Redirect logic (tenant-safe):
      if (wasReturnFlow) {
        // Keep existing behavior for returned-edit sessions
        navigate(tenantize('/approvals?tab=returned'), { replace: true });
        return;
      }

      // Normal submit: clear selection and go to Citizen Search dashboard
      clearScopedVisitContext();
      navigate(tenantize('/dashboard/operations'), { replace: true, state: { redirectedFrom: 'prescription-submit', focusSection: 'citizen-services', visitCompleted: true, citizenId, visitId } });
      return;
    }catch(e){ setActionError(e.message || 'Server error'); }
    finally{ setBusy(false); }
  }, [
    visitId, isIntern, save, user, isSubmittedOrApproved, statusIsReturned,
    navigate, clearScopedVisitContext, citizenId
  ]);

  // Approver path (FAB for non-interns)
  const saveAndApprove = useCallback(async () => {
    try{
      if (isSubmittedOrApproved) return;
      setActionError(''); setActionSuccess(''); setBusy(true);
      const ok = await save(); if (!ok) return;
      const actor = user?.name || user?.fullName || user?.email || '';
      const role = user?.role || user?.userType || '';
      await patchApproveOrSubmit(
        `/api/visits/${encodeURIComponent(visitId)}/prescription/approve`,
        { actor, role },
        `/api/visit-prescription/${encodeURIComponent(visitId)}/approve`
      );
      setForm(f => ({ ...f, status: 'approved' }));
      setActionSuccess('Approved');

      // For non-interns: clear scope & go back to Citizen dashboard (tenant-safe)
      clearScopedVisitContext();
      navigate(tenantize('/dashboard/operations'), { replace: true, state: { redirectedFrom: 'prescription-approve', focusSection: 'citizen-services', visitCompleted: true, citizenId, visitId } });
      return;
    }catch(e){ setActionError(e.message || 'Server error'); }
    finally{ setBusy(false); }
  }, [visitId, save, user, isSubmittedOrApproved, clearScopedVisitContext, navigate, citizenId]);

  // Preserve the standalone editor lifecycle exactly. The Workspace does not
  // attach this legacy FAB listener: Save Prescription must never submit an
  // intern Visit or self-approve a non-intern Visit.
  useEffect(() => {
    if (embedded) return undefined;

    const handler = async (e) => {
      if (isSubmittedOrApproved) return;
      const d = (e && e.detail) || {};
      if (d.visitId && d.visitId !== visitId) return;
      if (d.section && d.section !== 'prescription') return;
      if (isIntern) { await sendForApproval(); return; }
      await saveAndApprove();
    };
    window.addEventListener('encounter:save', handler);
    window.addEventListener('visit:save', handler);
    return () => {
      window.removeEventListener('encounter:save', handler);
      window.removeEventListener('visit:save', handler);
    };
  }, [embedded, visitId, isIntern, saveAndApprove, sendForApproval, isSubmittedOrApproved]);

  // editor helpers
  const updateMedicine = (idx,val) => { markDirty({ field:'medicine' }); setForm(f => {
    const list = Array.isArray(f.medicines) ? f.medicines.slice() : [];
    list[idx] = val || '';
    return { ...f, medicines: list, medicationReminders: syncMedicationReminders(f.medicationReminders, list) };
  });};
  const addMedicine = () => { markDirty({ field:'medicine' }); setForm(f => {
    const list = [ ...(Array.isArray(f.medicines)?f.medicines:[]), '' ];
    return { ...f, medicines: list, medicationReminders: syncMedicationReminders(f.medicationReminders, list) };
  }); };
  const removeMedicine = (idx) => { markDirty({ field:'medicine' }); setForm(f => {
    const list = Array.isArray(f.medicines) ? f.medicines.slice() : [];
    list.splice(idx,1); return { ...f, medicines: list, medicationReminders: syncMedicationReminders(f.medicationReminders, list) };
  });};
  const setIcdFromText = (text) => { markDirty({ field:'icdCodes' });
    const arr = String(text || '').split(/\r?\n|,/g).map(s => s.trim()).filter(Boolean).map(title => ({ title }));
    setForm(f => ({ ...f, icdCodes: arr }));
  };
  const icdText = useMemo(
    () => (Array.isArray(form.icdCodes) ? form.icdCodes.map(x => (x && x.title) || '').filter(Boolean).join('\n') : ''),
    [form.icdCodes]
  );

  const updateCareLogEntry = useCallback((groupKey, itemKey, patch) => {
    const fieldName = groupKey === 'opdProcedureLog' ? 'procedure' : 'counseling';
    markDirty({ field: fieldName, itemKey });
    setForm((prev) => {
      const group = prev[groupKey] && typeof prev[groupKey] === 'object' ? prev[groupKey] : {};
      const current = group[itemKey] && typeof group[itemKey] === 'object'
        ? group[itemKey]
        : { done: false, status: 'advised', notes: '', steps: {} };
      return {
        ...prev,
        [groupKey]: {
          ...group,
          [itemKey]: {
            ...current,
            ...patch,
          },
        },
      };
    });
  }, []);

  const updateCareLogToggle = useCallback((groupKey, itemKey, checked, stepsList) => {
    const nextSteps = Array.isArray(stepsList)
      ? stepsList.reduce((acc, stepLabel) => {
          acc[stepLabel] = false;
          return acc;
        }, {})
      : {};
    updateCareLogEntry(groupKey, itemKey, {
      done: checked,
      status: checked ? 'advised' : 'advised',
      notes: checked ? '' : '',
      steps: checked ? nextSteps : nextSteps,
    });
  }, [updateCareLogEntry]);

  const updateCareLogStep = useCallback((groupKey, itemKey, stepLabel, checked) => {
    const fieldName = groupKey === 'opdProcedureLog' ? 'procedure' : 'counseling';
    markDirty({ field: fieldName, itemKey, step: stepLabel });
    setForm((prev) => {
      const group = prev[groupKey] && typeof prev[groupKey] === 'object' ? prev[groupKey] : {};
      const current = group[itemKey] && typeof group[itemKey] === 'object'
        ? group[itemKey]
        : { done: false, status: 'advised', notes: '', steps: {} };
      return {
        ...prev,
        [groupKey]: {
          ...group,
          [itemKey]: {
            ...current,
            steps: {
              ...(current.steps || {}),
              [stepLabel]: checked,
            },
          },
        },
      };
    });
  }, []);

  const updateFollowUpEntry = useCallback((itemKey, patch) => {
    markDirty({ field: 'followUpPlan', itemKey });
    setForm((prev) => ({
      ...prev,
      followUpPlan: {
        ...(prev.followUpPlan || {}),
        [itemKey]: {
          ...((prev.followUpPlan && prev.followUpPlan[itemKey]) || {}),
          ...patch,
        },
      },
    }));
  }, []);

  const toggleFollowUpEntry = useCallback((itemKey, checked) => {
    const definition = FOLLOW_UP_DEFINITIONS.find((def) => def.key === itemKey);
    updateFollowUpEntry(itemKey, {
      enabled: checked,
      mode: definition?.defaultMode || itemKey,
      date: checked ? (((form.followUpPlan || {})[itemKey] || {}).date || '') : '',
      time: checked ? (((form.followUpPlan || {})[itemKey] || {}).time || '') : '',
      reason: checked ? (((form.followUpPlan || {})[itemKey] || {}).reason || '') : '',
      notes: checked ? (((form.followUpPlan || {})[itemKey] || {}).notes || '') : '',
      priority: checked ? (((form.followUpPlan || {})[itemKey] || {}).priority || 'routine') : 'routine',
    });
  }, [form.followUpPlan, updateFollowUpEntry]);

  const updateMedicationReminder = useCallback((idx, patch) => {
    markDirty({ field: 'medicationReminders', index: idx });
    setForm((prev) => {
      const reminders = Array.isArray(prev.medicationReminders) ? prev.medicationReminders.slice() : [];
      const current = reminders[idx] && typeof reminders[idx] === 'object'
        ? reminders[idx]
        : { enabled: false, medicine: String((prev.medicines || [])[idx] || ''), frequency: 'daily', times: ['', '', '', ''], notes: '' };
      reminders[idx] = {
        ...current,
        ...patch,
        times: Array.isArray((patch && patch.times) || current.times)
          ? (((patch && patch.times) || current.times).concat(['', '', '', '']).slice(0, 4))
          : ['', '', '', ''],
      };
      return { ...prev, medicationReminders: reminders };
    });
  }, []);

  const updateHealthReminder = useCallback((itemKey, patch) => {
    markDirty({ field: 'healthReminders', itemKey });
    setForm((prev) => ({
      ...prev,
      healthReminders: {
        ...(prev.healthReminders || {}),
        [itemKey]: {
          ...((prev.healthReminders && prev.healthReminders[itemKey]) || {}),
          ...patch,
        },
      },
    }));
  }, []);

  const selectedProcedureDefinitions = useMemo(
    () => PROCEDURE_DEFINITIONS.filter((def) => form.opdProcedureLog?.[def.key]?.done),
    [form.opdProcedureLog]
  );

  const selectedCounselingDefinitions = useMemo(
    () => COUNSELING_DEFINITIONS.filter((def) => form.opdCounselingLog?.[def.key]?.done),
    [form.opdCounselingLog]
  );

  const visitContext = useMemo(() => {
    if (embedded && visitProp) {
      return {
        visitId: visitProp.visitId || visitId,
        citizenId: visitProp.citizenId || citizenId,
        visitType: visitProp.visitType || visitProp.encounterType || '',
        visitCategory: visitProp.visitCategory || visitProp.serviceArea || '',
        serviceArea: visitProp.serviceArea || visitProp.visitCategory || '',
        clinicalPathway: visitProp.clinicalPathway || visitProp.clinicalFocus || '',
        clinicalFocus: visitProp.clinicalFocus || visitProp.clinicalPathway || '',
        complaintGroup: visitProp.complaintGroup || visitProp.primaryPresentingProblem || '',
        primaryPresentingProblem: visitProp.primaryPresentingProblem || visitProp.complaintGroup || '',
        maternalContext: visitProp.maternalContext || '',
        triagePriority: visitProp.triagePriority || '',
        ageGroupAtVisit: visitProp.ageGroupAtVisit || '',
        ageYearsAtVisit: visitProp.ageYearsAtVisit,
        citizenGender: visitProp.citizenGender || '',
        chiefComplaints: Array.isArray(visitProp.chiefComplaints) ? visitProp.chiefComplaints : [],
        activeClinicalModules: Array.isArray(visitProp.activeClinicalModules)
          ? visitProp.activeClinicalModules
          : [],
        additionalPathways: Array.isArray(visitProp.additionalPathways)
          ? visitProp.additionalPathways
          : [],
      };
    }

    const scoped = visitId ? jget(`activeVisitContext:${visitId}`) : null;
    return scoped || jget('activeVisitContext') || {};
  }, [embedded, citizenId, visitId, visitProp]);

  const currentVisitCategory = useMemo(
    () => String(visitContext?.visitCategory || query.get('visitCategory') || '').trim(),
    [visitContext, query]
  );
  const currentClinicalPathway = useMemo(
    () => String(visitContext?.clinicalPathway || query.get('clinicalPathway') || '').trim(),
    [visitContext, query]
  );
  const currentComplaintGroup = useMemo(
    () => String(visitContext?.complaintGroup || query.get('complaintGroup') || '').trim(),
    [visitContext, query]
  );
  const currentVisitType = useMemo(
    () => String(visitContext?.visitType || query.get('visitType') || '').trim(),
    [visitContext, query]
  );

  const activeClinicalModules = useMemo(
    () => Array.isArray(visitContext?.activeClinicalModules) ? visitContext.activeClinicalModules : [],
    [visitContext]
  );
  const prescriptionPlan = useMemo(
    () => getSectionModulePlan({ modules: activeClinicalModules, section: 'prescription' }),
    [activeClinicalModules]
  );
  const activeModuleLabels = useMemo(
    () => moduleLabels(activeClinicalModules),
    [activeClinicalModules]
  );

  const profileSummary = useMemo(() => {
    const parts = [];
    const ageDisplay = formatAgeAtVisit(visitProp || {}, citizenAge);
    if (ageDisplay) parts.push(`Age ${ageDisplay}`);
    if (citizenAgeGroup !== 'unknown') parts.push(ageGroupLabel(citizenAgeGroup));
    if (resolvedCitizen?.gender) parts.push(String(resolvedCitizen.gender));
    return parts.join(' • ');
  }, [citizenAge, citizenAgeGroup, resolvedCitizen, visitProp]);

  const contextChips = useMemo(() => {
    const out = [];
    if (currentVisitCategory) out.push(`Service Area: ${currentVisitCategory}`);
    if (currentClinicalPathway) out.push(`Clinical Focus: ${currentClinicalPathway}`);
    if (currentComplaintGroup) out.push(`Primary Presenting Problem: ${currentComplaintGroup}`);
    return out;
  }, [currentVisitCategory, currentClinicalPathway, currentComplaintGroup]);

  const recommendedProcedureKeys = useMemo(
    () => getRecommendedProcedureKeys({
      visitCategory: currentVisitCategory,
      clinicalPathway: currentClinicalPathway,
      complaintGroup: currentComplaintGroup,
      ageGroup: citizenAgeGroup,
      citizenGender,
      activeClinicalModules,
    }),
    [currentVisitCategory, currentClinicalPathway, currentComplaintGroup, citizenAgeGroup, citizenGender, activeClinicalModules]
  );

  const recommendedCounselingKeys = useMemo(
    () => getRecommendedCounselingKeys({
      visitCategory: currentVisitCategory,
      clinicalPathway: currentClinicalPathway,
      complaintGroup: currentComplaintGroup,
      ageGroup: citizenAgeGroup,
      citizenGender,
      activeClinicalModules,
    }),
    [currentVisitCategory, currentClinicalPathway, currentComplaintGroup, citizenAgeGroup, citizenGender, activeClinicalModules]
  );

  const visitWeightKg = useMemo(
    () => getVisitWeightKg(visitProp || {}),
    [visitProp]
  );

  const recommendedMedicineCategories = useMemo(
    () => getRecommendedMedicineCategories({
      visitCategory: currentVisitCategory,
      clinicalPathway: currentClinicalPathway,
      complaintGroup: currentComplaintGroup,
      ageGroup: citizenAgeGroup,
      activeClinicalModules,
    }),
    [currentVisitCategory, currentClinicalPathway, currentComplaintGroup, citizenAgeGroup, activeClinicalModules]
  );

  const selectedFollowUpCount = useMemo(
    () => FOLLOW_UP_DEFINITIONS.filter((def) => form.followUpPlan?.[def.key]?.enabled).length,
    [form.followUpPlan]
  );

  const selectedHealthReminderCount = useMemo(
    () => HEALTH_REMINDER_DEFINITIONS.filter((def) => form.healthReminders?.[def.key]?.enabled).length,
    [form.healthReminders]
  );

  const medicationReminderEnabledCount = useMemo(
    () => Array.isArray(form.medicationReminders)
      ? form.medicationReminders.filter((item) => item && item.enabled).length
      : 0,
    [form.medicationReminders]
  );

  const postVisitSuggestions = useMemo(
    () => createPostVisitSuggestions({
      visitCategory: currentVisitCategory,
      visitType: currentVisitType,
      procedureDefs: selectedProcedureDefinitions,
      counselingDefs: selectedCounselingDefinitions,
      medicationRemindersEnabled: medicationReminderEnabledCount > 0 || !!form.medicationRemindersEnabled,
    }),
    [
      currentVisitCategory,
      currentVisitType,
      selectedProcedureDefinitions,
      selectedCounselingDefinitions,
      medicationReminderEnabledCount,
      form.medicationRemindersEnabled,
    ]
  );

  // catalog (right rail)
  const categories = useMemo(() => {
    const set = { All: true };
    (medicineList || []).forEach(m => { if (m && m.category) set[m.category] = true; });
    return Object.keys(set);
  }, []);
  const filtered = useMemo(() => {
    const q = String(catalogQuery || '').toLowerCase();
    const rows = (medicineList || []).filter(m => {
      const catOk = catalogCat === 'All' || (m && m.category === catalogCat);
      if (!catOk) return false;
      if (!q) return true;
      const hay = String((m && m.name) || '') + ' ' + String((m && m.notes) || '') + ' ' + String((m && m.category) || '');
      return hay.toLowerCase().includes(q);
    });
    if (catalogCat !== 'All' || recommendedMedicineCategories.length === 0) return rows;
    return [...rows].sort((a, b) => {
      const aSuggested = recommendedMedicineCategories.includes(a?.category) ? 0 : 1;
      const bSuggested = recommendedMedicineCategories.includes(b?.category) ? 0 : 1;
      return aSuggested - bSuggested;
    });
  }, [catalogQuery, catalogCat, recommendedMedicineCategories]);

  const addFromCatalog = (m) => {
    if (disabled || !m) return;
    const usePaediatricReference = citizenAge != null && citizenAge < 18;
    const referenceDose = usePaediatricReference
      ? (m.pediatricDose || m.adultDose || '')
      : (m.adultDose || '');
    const dose = referenceDose ? ` — ${referenceDose}` : '';
    markDirty({ field:'medicine' });
    setForm(f => ({
      ...f,
      medicines: [ ...(Array.isArray(f.medicines)?f.medicines:[]), String(m.name || '') + dose ]
    }));
  };

  const showReturnedEditBanner = (statusIsReturned || editFlag === visitId) && isIntern && !isSubmittedOrApproved && !disabled;

  return (
    <div
      className={
        embedded
          ? ''
          : 'p-6 bg-white dark:bg-[#181a1b] min-h-screen text-black dark:text-white'
      }
    >
      {!embedded && <EncounterHeader visitId={visitId}/>}
      {loading ? <CircularProgress/> : (
        <>
          {loadError && <Alert severity="error" sx={{mb:2}}>{loadError}</Alert>}
          {saveError && <Alert severity="error" sx={{mb:2}}>{saveError}</Alert>}
          {saveSuccess && <Alert severity="success" sx={{mb:2}}>{saveSuccess}</Alert>}
          {actionError && <Alert severity="error" sx={{mb:2}}>{actionError}</Alert>}
          {actionSuccess && <Alert severity="success" sx={{mb:2}}>{actionSuccess}</Alert>}
          {!embedded && showReturnedEditBanner && (
            <Alert severity="info" sx={{mb:2}}>
              This visit was <strong>returned for edits</strong>. Update the prescription and use the <em>Send for Approval</em> button.
            </Alert>
          )}

          {!!resolvedCitizen?.citizenId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {resolvedCitizen?.name || 'Citizen selected'}{profileSummary ? ` • ${profileSummary}` : ''}
            </Alert>
          )}

          {contextChips.length > 0 && (
            <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
              {contextChips.map((item) => (
                <Chip key={item} size="small" color="primary" variant="outlined" label={item} />
              ))}
            </Box>
          )}

          {activeModuleLabels.length > 0 && (
            <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
              {activeModuleLabels.map((item) => (
                <Chip key={item.key} size="small" variant="outlined" label={`Focused module: ${item.label}`} />
              ))}
            </Box>
          )}

          {prescriptionPlan.prompts.length > 0 && (
            <Alert severity="info" sx={{ mb:2 }}>
              <Box component="ul" sx={{ m:0, pl:2.25 }}>
                {prescriptionPlan.prompts.slice(0, 6).map((prompt) => (
                  <Box component="li" key={prompt} sx={{ mb:0.5 }}>{prompt}</Box>
                ))}
              </Box>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            {embedded
              ? 'This Prescription section uses the persisted Visit context. Save Prescription stores this section only; Visit submission, return and finalization remain separate Visit-level actions.'
              : 'This prescription page uses the selected citizen’s age group and visit context to highlight likely procedures, counseling, follow-up, reminders, and training suggestions while preserving the existing standalone workflow.'}
          </Alert>

          <Typography variant="h5">Prescription {disabled ? '(Read-only)' : ''}</Typography>
          <Divider sx={{my:2}}/>

          <Grid container spacing={2}>
            {/* left editor */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Typography variant="h6" sx={{mb:1}}>Differential Diagnosis</Typography>
                <TextField
                  label="List likely diagnoses"
                  value={form.differentialDx}
                  onChange={(e)=>{ setForm(f=>({...f,differentialDx:e.target.value})); markDirty({ field:'differentialDx' }); }}
                  fullWidth
                  disabled={disabled}
                  multiline
                  minRows={2}
                />
              </Paper>

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{mb:1}}>ICD Codes / Diagnoses</Typography>
                    <TextField
                      label="One per line"
                      value={icdText}
                      onChange={(e)=>{ setIcdFromText(e.target.value); }}
                      fullWidth
                      disabled={disabled}
                      multiline
                      minRows={7}
                      placeholder={'E11 Type 2 diabetes\nI10 Essential hypertension'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{mb:1}}>Advice & Notes</Typography>
                    <TextField
                      label="Advice"
                      value={form.advice}
                      onChange={(e)=>{ setForm(f=>({...f,advice:e.target.value})); markDirty({ field:'advice' }); }}
                      fullWidth
                      disabled={disabled}
                      multiline
                      minRows={3}
                    />
                    <Box sx={{height:8}}/>
                    <TextField
                      label="Notes"
                      value={form.notes}
                      onChange={(e)=>{ setForm(f=>({...f,notes:e.target.value})); markDirty({ field:'notes' }); }}
                      fullWidth
                      disabled={disabled}
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Box sx={{display:'flex',alignItems:'center',mb:1}}>
                  <Box>
                    <Typography variant="h6">Medicines</Typography>
                    <Typography variant="body2" color="text.secondary">Core treatment entry. Pathway-based catalog focus is advisory; final selection remains the clinician's decision.</Typography>
                  </Box>
                  {!disabled && (
                    <Tooltip title="Add an empty medicine row">
                      <span>
                        <Button startIcon={<Add/>} size="small" variant="outlined" onClick={()=>addMedicine()}>
                          Add row
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>

                {Array.isArray(form.medicines) && form.medicines.length > 0 ? (
                  form.medicines.map((m,idx)=>(
                    <Grid key={idx} container spacing={1} alignItems="center" sx={{mb:1}}>
                      <Grid item xs>
                        <TextField
                          fullWidth
                          label={`Medicine #${idx+1} (name, dose, frequency)`}
                          value={m}
                          onChange={(e)=>{ updateMedicine(idx,e.target.value); }}
                          disabled={disabled}
                        />
                      </Grid>
                      {!disabled && (
                        <Grid item>
                          <IconButton aria-label="Remove" onClick={()=>removeMedicine(idx)} size="small">
                            <Delete fontSize="small"/>
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  ))
                ) : <Typography variant="body2" color="text.secondary">No medicines added.</Typography>}
              </Paper>

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Typography variant="h6" sx={{mb:1}}>Contextual Suggestions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb:1.5}}>
                  Suggested items below are based on the selected citizen profile and Add Visit context. You can still record any actually performed procedure or counseling item.
                </Typography>
                <Box sx={{display:'flex', gap:1, flexWrap:'wrap', mb:2}}>
                  {recommendedProcedureKeys.map((key) => {
                    const item = PROCEDURE_DEFINITIONS.find((x) => x.key === key);
                    return item ? <Chip key={`rp-${key}`} size="small" color="primary" variant="outlined" label={`Suggested procedure: ${item.shortLabel}`} /> : null;
                  })}
                  {recommendedCounselingKeys.map((key) => {
                    const item = COUNSELING_DEFINITIONS.find((x) => x.key === key);
                    return item ? <Chip key={`rc-${key}`} size="small" color="secondary" variant="outlined" label={`Suggested counseling: ${item.shortLabel}`} /> : null;
                  })}
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:2, mb:2, flexWrap:'wrap'}}>
                  <Box>
                    <Typography variant="h6">OPD Procedure / Counseling Performed</Typography>
                    <Typography variant="body2" color="text.secondary">
                      These flags belong to the citizen care record and are used for prescription completeness and OPD census.
                    </Typography>
                  </Box>
                  <Box sx={{display:'flex', gap:1, flexWrap:'wrap'}}>
                    <Chip size="small" color="primary" variant="outlined" label={`Procedures selected: ${procedureSelectionCount}`} />
                    <Chip size="small" color="secondary" variant="outlined" label={`Counseling selected: ${counselingSelectionCount}`} />
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" sx={{ mb: 1 }}>Procedure Performed</FormLabel>
                      <FormGroup>
                        {PROCEDURE_DEFINITIONS.map((item) => (
                          <FormControlLabel
                            key={item.key}
                            control={
                              <Checkbox
                                checked={!!form.opdProcedureLog?.[item.key]?.done}
                                onChange={(e) => updateCareLogToggle('opdProcedureLog', item.key, e.target.checked, item.steps)}
                                disabled={disabled}
                              />
                            }
                            label={item.label}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" sx={{ mb: 1 }}>Counseling / Advice Given</FormLabel>
                      <FormGroup>
                        {COUNSELING_DEFINITIONS.map((item) => (
                          <FormControlLabel
                            key={item.key}
                            control={
                              <Checkbox
                                checked={!!form.opdCounselingLog?.[item.key]?.done}
                                onChange={(e) => updateCareLogToggle('opdCounselingLog', item.key, e.target.checked, item.steps)}
                                disabled={disabled}
                              />
                            }
                            label={item.label}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {(selectedProcedureDefinitions.length > 0 || selectedCounselingDefinitions.length > 0) && (
                <Paper variant="outlined" sx={{p:2, mb:2}}>
                  <Typography variant="h6" sx={{mb:0.5}}>Linked Guided Procedure / Counseling Flow</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb:2}}>
                    Complete the step-by-step log only for items checked above. This records user guidance/workflow detail while keeping census counts tied to the OPD care record.
                  </Typography>

                  {selectedProcedureDefinitions.map((item) => {
                    const entry = form.opdProcedureLog?.[item.key] || {};
                    return (
                      <Paper key={item.key} variant="outlined" sx={{p:2, mb:2}}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" sx={{mb:1}}>{item.shortLabel}</Typography>
                            <TextField
                              select
                              fullWidth
                              label="How was this handled?"
                              value={entry.status || 'advised'}
                              onChange={(e) => updateCareLogEntry('opdProcedureLog', item.key, { status: e.target.value })}
                              disabled={disabled}
                              {...fieldProps}
                            >
                              {CREDIT_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </TextField>
                            <Box sx={{height:12}} />
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              label="Procedure notes"
                              value={entry.notes || ''}
                              onChange={(e) => updateCareLogEntry('opdProcedureLog', item.key, { notes: e.target.value })}
                              disabled={disabled}
                              placeholder="Brief procedural notes, materials used, advice given, or reason if only advised."
                              {...fieldProps}
                            />
                          </Grid>

                          <Grid item xs={12} md={8}>
                            <Typography variant="body2" color="text.secondary" sx={{mb:1}}>
                              Step-by-step procedure flow
                            </Typography>
                            <FormGroup>
                              {item.steps.map((stepLabel) => (
                                <FormControlLabel
                                  key={stepLabel}
                                  control={
                                    <Checkbox
                                      checked={!!entry.steps?.[stepLabel]}
                                      onChange={(e) => updateCareLogStep('opdProcedureLog', item.key, stepLabel, e.target.checked)}
                                      disabled={disabled}
                                    />
                                  }
                                  label={stepLabel}
                                />
                              ))}
                            </FormGroup>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}

                  {selectedCounselingDefinitions.map((item) => {
                    const entry = form.opdCounselingLog?.[item.key] || {};
                    return (
                      <Paper key={item.key} variant="outlined" sx={{p:2, mb:2}}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" sx={{mb:1}}>{item.shortLabel}</Typography>
                            <TextField
                              select
                              fullWidth
                              label="How was this handled?"
                              value={entry.status || 'advised'}
                              onChange={(e) => updateCareLogEntry('opdCounselingLog', item.key, { status: e.target.value })}
                              disabled={disabled}
                              {...fieldProps}
                            >
                              {CREDIT_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </TextField>
                            <Box sx={{height:12}} />
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              label="Counseling notes"
                              value={entry.notes || ''}
                              onChange={(e) => updateCareLogEntry('opdCounselingLog', item.key, { notes: e.target.value })}
                              disabled={disabled}
                              placeholder="Brief summary of points explained to the citizen / family."
                              {...fieldProps}
                            />
                          </Grid>

                          <Grid item xs={12} md={8}>
                            <Typography variant="body2" color="text.secondary" sx={{mb:1}}>
                              Step-by-step counseling flow
                            </Typography>
                            <FormGroup>
                              {item.steps.map((stepLabel) => (
                                <FormControlLabel
                                  key={stepLabel}
                                  control={
                                    <Checkbox
                                      checked={!!entry.steps?.[stepLabel]}
                                      onChange={(e) => updateCareLogStep('opdCounselingLog', item.key, stepLabel, e.target.checked)}
                                      disabled={disabled}
                                    />
                                  }
                                  label={stepLabel}
                                />
                              ))}
                            </FormGroup>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Paper>
              )}

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:2, mb:2, flexWrap:'wrap'}}>
                  <Box>
                    <Typography variant="h6">Follow-up Planning</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Schedule the next physical review, telemedicine follow-up, or home visit from this encounter.
                    </Typography>
                  </Box>
                  <Chip size="small" color="primary" variant="outlined" label={`Follow-ups selected: ${selectedFollowUpCount}`} />
                </Box>

                <Grid container spacing={2}>
                  {FOLLOW_UP_DEFINITIONS.map((item) => {
                    const entry = form.followUpPlan?.[item.key] || {};
                    return (
                      <Grid item xs={12} md={6} key={item.key}>
                        <Paper variant="outlined" sx={{p:2, height:'100%'}}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!entry.enabled}
                                onChange={(e) => toggleFollowUpEntry(item.key, e.target.checked)}
                                disabled={disabled}
                              />
                            }
                            label={item.label}
                          />
                          {entry.enabled && (
                            <Grid container spacing={1} sx={{mt:0.5}}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  type="date"
                                  fullWidth
                                  label="Follow-up date"
                                  value={entry.date || ''}
                                  onChange={(e) => updateFollowUpEntry(item.key, { date: e.target.value })}
                                  disabled={disabled}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  type="time"
                                  fullWidth
                                  label="Follow-up time"
                                  value={entry.time || ''}
                                  onChange={(e) => updateFollowUpEntry(item.key, { time: e.target.value })}
                                  disabled={disabled}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Reason / indication"
                                  value={entry.reason || ''}
                                  onChange={(e) => updateFollowUpEntry(item.key, { reason: e.target.value })}
                                  disabled={disabled}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Priority"
                                  value={entry.priority || 'routine'}
                                  onChange={(e) => updateFollowUpEntry(item.key, { priority: e.target.value })}
                                  disabled={disabled}
                                  {...fieldProps}
                                >
                                  <MenuItem value="routine">Routine</MenuItem>
                                  <MenuItem value="priority">Priority</MenuItem>
                                  <MenuItem value="urgent">Urgent</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  label="Scheduling notes"
                                  value={entry.notes || ''}
                                  onChange={(e) => updateFollowUpEntry(item.key, { notes: e.target.value })}
                                  disabled={disabled}
                                  placeholder="Any reminder for the next intern / non-intern team handling this follow-up."
                                />
                              </Grid>
                            </Grid>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:2, mb:2, flexWrap:'wrap'}}>
                  <Box>
                    <Typography variant="h6">Citizen Reminders / Alerts</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Set medicine reminders and health-action reminders for the citizen portal.
                    </Typography>
                  </Box>
                  <Box sx={{display:'flex', gap:1, flexWrap:'wrap'}}>
                    <Chip size="small" color="primary" variant="outlined" label={`Medicine reminders: ${medicationReminderEnabledCount}`} />
                    <Chip size="small" color="secondary" variant="outlined" label={`Health reminders: ${selectedHealthReminderCount}`} />
                  </Box>
                </Box>

                <Paper variant="outlined" sx={{p:2, mb:2}}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form.medicationRemindersEnabled}
                        onChange={(e) => {
                          markDirty({ field: 'medicationRemindersEnabled' });
                          setForm((prev) => ({ ...prev, medicationRemindersEnabled: e.target.checked }));
                        }}
                        disabled={disabled}
                      />
                    }
                    label="Enable citizen reminders for prescribed medicines"
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{mt:1}}
                    label="Medicine reminder notes"
                    value={form.medicationReminderNotes || ''}
                    onChange={(e) => {
                      markDirty({ field: 'medicationReminderNotes' });
                      setForm((prev) => ({ ...prev, medicationReminderNotes: e.target.value }));
                    }}
                    disabled={disabled}
                    placeholder="Optional note to show with medicine reminders in the citizen portal."
                  />
                  {Array.isArray(form.medicationReminders) && form.medicationReminders.length > 0 && (
                    <Box sx={{mt:2}}>
                      <Typography variant="subtitle2" sx={{mb:1}}>Per-medicine reminder timing</Typography>
                      {form.medicationReminders.map((item, idx) => (
                        <Paper key={`med-reminder-${idx}`} variant="outlined" sx={{p:1.5, mb:1.5}}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!item.enabled}
                                onChange={(e) => updateMedicationReminder(idx, { enabled: e.target.checked })}
                                disabled={disabled}
                              />
                            }
                            label={item.medicine || `Medicine #${idx + 1}`}
                          />
                          {item.enabled && (
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Frequency"
                                  value={item.frequency || 'daily'}
                                  onChange={(e) => updateMedicationReminder(idx, { frequency: e.target.value })}
                                  disabled={disabled}
                                />
                              </Grid>
                              {[0, 1, 2, 3].map((timeIdx) => (
                                <Grid item xs={12} sm={3} key={`time-${timeIdx}`}>
                                  <TextField
                                    type="time"
                                    fullWidth
                                    label={`Reminder ${timeIdx + 1}`}
                                    value={(item.times && item.times[timeIdx]) || ''}
                                    onChange={(e) => {
                                      const nextTimes = Array.isArray(item.times) ? item.times.slice(0, 4) : ['', '', '', ''];
                                      while (nextTimes.length < 4) nextTimes.push('');
                                      nextTimes[timeIdx] = e.target.value;
                                      updateMedicationReminder(idx, { times: nextTimes });
                                    }}
                                    disabled={disabled}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              ))}
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  label="Reminder notes"
                                  value={item.notes || ''}
                                  onChange={(e) => updateMedicationReminder(idx, { notes: e.target.value })}
                                  disabled={disabled}
                                  placeholder="Optional citizen-facing reminder note for this medicine."
                                />
                              </Grid>
                            </Grid>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{p:2}}>
                  <Typography variant="subtitle2" sx={{mb:1}}>Other health-action reminders</Typography>
                  <Grid container spacing={2}>
                    {HEALTH_REMINDER_DEFINITIONS.map((item) => {
                      const entry = form.healthReminders?.[item.key] || {};
                      return (
                        <Grid item xs={12} md={6} key={item.key}>
                          <Paper variant="outlined" sx={{p:1.5, height:'100%'}}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!entry.enabled}
                                  onChange={(e) => updateHealthReminder(item.key, { enabled: e.target.checked })}
                                  disabled={disabled}
                                />
                              }
                              label={item.label}
                            />
                            {entry.enabled && (
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    label="Frequency"
                                    value={entry.frequency || 'daily'}
                                    onChange={(e) => updateHealthReminder(item.key, { frequency: e.target.value })}
                                    disabled={disabled}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    type="time"
                                    fullWidth
                                    label="Reminder time"
                                    value={entry.time || ''}
                                    onChange={(e) => updateHealthReminder(item.key, { time: e.target.value })}
                                    disabled={disabled}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Reminder notes"
                                    value={entry.notes || ''}
                                    onChange={(e) => updateHealthReminder(item.key, { notes: e.target.value })}
                                    disabled={disabled}
                                    placeholder="Optional citizen-facing reminder instruction."
                                  />
                                </Grid>
                              </Grid>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Paper>

              <Paper variant="outlined" sx={{p:2, mb:2}}>
                <Typography variant="h6" sx={{mb:0.5}}>Suggested Training Logs from This Visit</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb:2}}>
                  These are suggestions only. The visit remains the service record; guided completion is still needed for academic credit.
                </Typography>

                <Paper variant="outlined" sx={{p:2, mb:2}}>
                  <Typography variant="subtitle1" sx={{mb:0.5}}>Primary suggested log</Typography>
                  <Typography variant="body1">{postVisitSuggestions.primary.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{postVisitSuggestions.primary.description}</Typography>
                </Paper>

                {postVisitSuggestions.optional.length > 0 ? (
                  <Grid container spacing={2}>
                    {postVisitSuggestions.optional.map((item) => (
                      <Grid item xs={12} md={6} key={item.key}>
                        <Paper variant="outlined" sx={{p:2, height:'100%'}}>
                          <Typography variant="subtitle2">{item.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.reason}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info">No additional optional guided logs are suggested from the current selections yet.</Alert>
                )}
              </Paper>

            </Grid>

            {/* right catalog */}
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{p:2, position:'sticky', top:16}}>
                <Typography variant="h6" sx={{mb:1}}>Essential Medicines</Typography>
                <Alert severity={visitWeightKg ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
                  {visitWeightKg
                    ? `Visit weight available: ${visitWeightKg} kg. Automatic weight-based calculation will be shown only for medicines with a reviewed structured dose rule.`
                    : 'No reliable Visit weight is available yet. Weight-based calculation is therefore disabled.'}
                </Alert>
                {recommendedMedicineCategories.length > 0 && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Suggested catalog focus for this visit: {recommendedMedicineCategories.join(', ')}. Other medicine categories remain available below.
                  </Alert>
                )}
                <Box sx={{display:'flex',gap:1,flexWrap:'wrap',mb:1}}>
                  {categories.map((c) => (
                    <Chip
                      key={c}
                      label={c}
                      onClick={()=>{ setCatalogCat(c); }}
                      color={catalogCat===c?'primary':'default'}
                      variant={catalogCat===c?'filled':'outlined'}
                      size="small"
                    />
                  ))}
                </Box>
                <TextField
                  value={catalogQuery}
                  onChange={(e)=>{ setCatalogQuery(e.target.value); }}
                  placeholder="Search name, use, dose…"
                  fullWidth
                  InputProps={{startAdornment:(<InputAdornment position="start"><Search fontSize="small"/></InputAdornment>)}}
                  sx={{mb:1}}
                />
                <Box sx={{maxHeight:460, overflowY:'auto', pr:1}}>
                  {filtered.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No matches.</Typography>
                  )}
                  {filtered.map((m,i)=>{
                    const doseSupport = buildMedicationSupport(m, { weightKg: visitWeightKg });
                    return (
                    <Box key={i} sx={{p:1, borderRadius:1, '&:hover':{bgcolor:'action.hover'}}}>
                      <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                        <Typography variant="subtitle2" sx={{flex:1}}>{m?.name}</Typography>
                        {m?.category && <Chip size="small" label={m.category}/>}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Adult: {m?.adultDose || '—'}{m?.pediatricDose ? (' • Paed: ' + m.pediatricDose) : ''}
                      </Typography>
                      {m?.notes && <Typography variant="caption" display="block" color="text.secondary">{m.notes}</Typography>}
                      <Typography variant="caption" display="block" color={doseSupport.status === 'calculated' ? 'success.main' : 'text.secondary'}>
                        {doseSupport.status === 'calculated'
                          ? `Reviewed weight-based calculation: ${doseSupport.calculated.minMgPerDose}–${doseSupport.calculated.maxMgPerDose} mg per dose${doseSupport.calculated.minMlPerDose != null ? ` (${doseSupport.calculated.minMlPerDose}–${doseSupport.calculated.maxMlPerDose} mL)` : ''}`
                          : doseSupport.message}
                      </Typography>
                      {!disabled && (
                        <Box sx={{mt:0.5}}>
                          <Button size="small" variant="text" onClick={()=>addFromCatalog(m)} startIcon={<Add fontSize="small"/>}>
                            Add
                          </Button>
                        </Box>
                      )}
                      <Divider sx={{my:1}}/>
                    </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {(disabled || isSubmittedOrApproved) && (
            <Box sx={{mt:2}}>
              <Chip size="small" label={isSubmittedOrApproved ? 'Submitted' : 'Read-only'} />
            </Box>
          )}
        </>
      )}
    </div>
  );
}
